import React, { useRef } from "react";
import {
  referenceHtml,
  referenceTootKit,
  sectionStyle,
  h1style,
  h2style,
  h3style,
  subHeadingStyle,
  getTextContentHeading,
  DividerBorderStyle,
  paraStyle,
  tableCaption,
  getCircleTypeListItem,
  customHeading4,
  generateUnOrderedList,
  subHeadingContent,
} from "./ChapterList";
import objective from "../../../assets/SvgIcons/obj.svg";
import user from "../../../assets/SvgIcons/users.svg";
import designConsideration from "../../../assets/SvgIcons/design-consideration.svg";
import adjacencies from "../../../assets/SvgIcons/adjacencies.svg";
import Divider from "@mui/material/Divider";

import CaptionWithBorders1 from "../../../components/ChapterImage/Image";
import officeSpace3 from "../../../assets/Images/Office Space Images/officeSpace3.png";
import officeSpace4 from "../../../assets/Images/Office Space Images/officeSpace4.jpg";
import officeSpace5 from "../../../assets/Images/Office Space Images/officeSpace5.jpg";
import { FigureDesc53, ImageDesc62 } from "../../../utils/tmp/ImageDescription/RecordOfficeID";

const RecordsOffices = ({ goToSection }) => {
  const referencesRef = useRef(null);
  const reference = (_data, number) => {
    return referenceTootKit(_data, number, handleScrollToReferences);
  };

  const handleScrollToReferences = () => {
    if (referencesRef.current) {
      referencesRef.current.scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: "smooth",
      });
    }
  };

  const referenceStyle = {
    color: "black",
    fontFamily: "GT Super Text",
    fontSize: " 24px",
    fontWeight: "700",
    letterSpacing: "0px",
    textAlign: "left",
    fontStyle: "italic",
    cursor: "pointer",
    marginBottom: "10px",
  };

  const referenceData = [
    {
      content: "e-Courts Phase III Vision Document. ",
    },
    {
      content:
        "Aashima Kakkar, 'How are Records maintained in High Courts? What is the procedure to destroy Court Records?' (LawInsider, 2 June 2021)",
      link: "https://www.lawinsider.in/columns/how-are-records-maintained-in-high-Courts-what-is-the-procedure-to-destroy-Court-records",
      description:
        " <https://www.lawinsider.in/columns/how-are-records-maintained-in-high-Courts-what-is-the-procedure-to-destroy-Court-records> ",
    },
    {
      content:
        "Records Management and Storage (The Courthouse: A Guide to Planning & Design)",
      link: "https://www.ncsc.org/courthouseplanning/the-courthouse/records",
      description:
        " <https://www.ncsc.org/courthouseplanning/the-courthouse/records> ",
    },
  ];

  const ObjectivesObject = {
    icon: <img src={objective} />,
    heading: "Objectives",
    data: [
      <>
        <span style={{ color: "#DC002D" }}>
          Provide adequate space for records{" "}
        </span>{" "}
        of all disposed cases, in accordance with the timelines for storage
        mentioned in the relevant Civil/Criminal Rules;
      </>,
      <>
        <span style={{ color: "#DC002D" }}>
          Ensure safety of the physical records being stored{" "}
        </span>
        by taking precautionary measures to mitigate damage due to factors such
        as fire, white ants, moisture, etc.;
      </>,
      <>
        Ensure
        <span style={{ color: "#DC002D" }}>
          {" "}
          adequate light and ventilation{" "}
        </span>
        in the Record Room;
      </>,
      <>
        <span style={{ color: "#DC002D" }}>
          Ensure that the Record Room is secure{" "}
        </span>
        by providing a limited number of doors that can be manned for security
        reasons;
      </>,
      <>
        Ensure that the Record Room is
        <span style={{ color: "#DC002D" }}>
          {" "}
          accessible for diverse user groups
        </span>
        ;
      </>,
      <>
        <span style={{ color: "#DC002D" }}>
          Provide technologies for preserving old records
        </span>
        , such as document imaging facilities;
      </>,
      <>
        Ensure that all
        <span style={{ color: "#DC002D" }}>
          {" "}
          records are digitised to limit the storage space necessary{" "}
        </span>
        , as under the directions of the eCommittee of the Supreme Court;
        {reference(referenceData[0], 1)} and
      </>,
      <>
        Design the Record Room keeping in mind the{" "}
        <span style={{ color: "#DC002D" }}>
          need for future expansion of the space
        </span>
        .
      </>,
    ],

    isImage: true,
  };

  const usersObject = {
    icon: <img src={user} />,
    heading: "Users",
    data: [
      "Registrar and other Administrative Officers",
      "Staff at the Record Room",
      "Advocates",
    ],

    isImage: true,
  };

  const Adjacencies = {
    icon: <img src={adjacencies} />,
    heading: "Adjacencies",
    data: [
      <>
        The Record Room must be in close proximity to the Copying and Dispatch
        Section (
        <span
          style={sectionStyle}
          onClick={() => goToSection("filingSection", "headingFilingSection")}
        >
          Section 2.5.A
        </span>
        ) to ensure ease of communication and movement of records.
      </>,
      <>
        To facilitate digitisation, the Record Room must also have easy access
        to the Digitisation Centre (
        <span
          style={sectionStyle}
          onClick={() =>
            goToSection("officeSpaces", "officeSpacesMultiPersonWorkspace")
          }
        >
          Section 2.5.C
        </span>
        ). If the Digitisation Centre is not within the Court Complex, it must
        be ensured that the Record Room is in close proximity to one of the
        exits of the Court building so as to ensure easy movement of files in
        bulk.
      </>,
    ],

    isImage: true,
  };

  const designItem = [
    "Shelving: fixed lateral shelving, hanging open-box files, movable lateral shelving, vertical drawer filing, and mechanised filing;",
    "Specialised filing equipment and supplies: card-filing equipment, top or open reference files, file folders and supports; and",
    "Computer-product-filing equipment: for printouts, tapes, disks, platter, and microfilm-filing equipment.",
  ];

  const DesignConsiderations = [
    {
      icon: <img src={designConsideration} />,
      heading: "Design Considerations",
      items: [
        "The design of the Record Room must ensure that there is adequate space between the aisles for diverse user groups, including Persons with Disabilities.",
        "The space within the Record Room must be adequate for manoeuvring the trolleys used for bringing in files and other documents. ",
        <>
          All physical records must be kept between stiff covers to prevent
          fraying and folding before transferring them to the Record Room.
          Exhibits which are required to be stored must be unfolded and placed
          in envelopes for secure storage.{reference(referenceData[1], 2)}
        </>,
        "A Compact Storage System must be used to store voluminous records, and to keep the records free from dust or pest problems. A clear digitised cataloguing system must be maintained for easy retrieval of records when needed.",
        "Storage shelf heights/widths must be adjustable to have greater versatility in storage capabilities. ",
        <>
          Adequate filing system facilities must be provided for the storage of
          records, including:{reference(referenceData[2], 3)}{" "}
          {getCircleTypeListItem(designItem, true)}
        </>,
      ],
    },

    {
      heading: "Office Space for Record Room Staff",
      items: [
        "The Record Room must have adequate segregated designated workspace for the Record Room staff. ",
        "The room must be well-ventilated and be equipped with adequate ergonomic seating, storage facilities, and provision of adequate technology including document imaging technology such as scanners, digital record keeping systems, etc.",
        "A separate bench or desk must be provided to store the records as they are brought in by the Court Clerks and before they are categorised and properly stored. ",
        "A counter must be provided for the Record Room staff to receive and categorise the files that are being brought in by the Court Clerks. ",
        "Provision must also be made for storing personal belongings of the staff. ",
      ],
    },
  ];

  return (
    <div>
      <h1 id="handbookPrinciple" style={h1style}>
        PART C - THE HANDBOOK
      </h1>
      <h2 style={h2style}>
        <span style={{ fontSize: "24px" }}>2. </span>
        ORGANIZATION AND DESIGN OF SPACES OF THE COURT COMPLEX
      </h2>
      <h3 style={h3style}>
        <span style={{ fontSize: "20px" }}>2.5. </span>
        Administrative Wing
      </h3>

      <h4 id="managemenetRecord" style={h3style}>
        <span style={{ fontSize: "20px" }}>2.5.</span>D. Offices for Management
        of Records
      </h4>

      <h4 id="pendingBranch" style={subHeadingStyle}>
        I. Pending Branch{" "}
      </h4>
      <p style={paraStyle}>
        Generally, each Courtroom has its own pending section or a pending
        section shared between two or more Courts, depending on the size and
        jurisdiction of the Court. Lawyers may approach the pending branch to
        ascertain the next date of hearing and other details of orders passed in
        pending cases. Therefore, the pending branch has certain public-facing
        functions.
      </p>
      <p style={paraStyle}>
        The pending branch must have smart storage systems, and data entry
        operators for digitisation. The smart storage systems may be similar to
        the systems used in the Record Room. For further details, please refer
        to the following Section.
      </p>
      <p style={paraStyle}>
        Adequate desks and workstations must be provided for the pending branch
        staff. Case display monitors must be provided.
      </p>
      <DividerBorderStyle />

      <h4 id="recordRoomBranch" style={subHeadingStyle}>
        II. Record Room
      </h4>
      <p style={paraStyle}>
        The Record Room in a District Court maintains the judicial records of
        disposed case files from the Court Complex. There are two types of case
        files being maintained by the Court, ie., Civil and Criminal case files.
        Depending on the size of the Court, the Record Rooms may be separate or
        combined. After a case has been disposed of, the disposed files are sent
        to the Record Room on consignment by the Court Clerks/Shirestedars.
      </p>

      {getTextContentHeading(ObjectivesObject)}
      {getTextContentHeading(usersObject)}
      {getTextContentHeading(Adjacencies)}
      {subHeadingContent(DesignConsiderations)}

      <CaptionWithBorders1
        image={[officeSpace3]}
        isZoom={true}
        caption={"Figure 53: Typical floor plan of a Record Room"}
        zoomImgStyle={{ height: "500px" }}
        imageDescription={FigureDesc53}
      />

      <CaptionWithBorders1
        image={[officeSpace4, officeSpace5]}
        caption={
          "Image 62: Examples of Compact Storage System at the Record Room in the Bhadrak District Court, Odisha"
        }
        creditLabel="Image Credit"
        imageCredit="369 Ochre Studio"
        zoomImgStyle={{ height: "400px" }}
        imageDescription={ImageDesc62}
      />

      <h2 ref={referencesRef} id="officeRef" style={referenceStyle}>
        References
        <Divider />
      </h2>

      {referenceHtml(referenceData)}
    </div>
  );
};

export default RecordsOffices;
